import moment from 'moment';
import {
  defaultTimelineUnitCount,
  hour,
  scaleSubdivisionDefaults,
} from './constants';
import { TimelineScale } from './types';

export function momentToTimestamp(date: moment.Moment): number {
  return Number(date.format('x'));
}

export function momentRoundedToHour(
  date: moment.Moment,
  targetHour: number = 0,
): moment.Moment {
  return date.set({
    hours: targetHour,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
}

/**
 * Determines the best logical start time for the timeline ruler based on the current time and scale.
 * @param time The initial time to round down to the nearest start time
 * @param scale The scale of the timeline, e.g., 'hours', 'days', 'weeks'
 * @returns The nearest start time appropriate for the current time and scale
 */
export function getNearestStartTime(
  time: number,
  scale: TimelineScale,
): number {
  const date = moment(time);

  // If the scale is days, round down to the nearest day at midnight
  if (scale === 'days') {
    return momentToTimestamp(momentRoundedToHour(date));
  }

  // If the scale is weeks, round down to the nearest Sunday at midnight
  if (scale === 'weeks') {
    const day = date.day();
    const diff = date.date() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    return momentToTimestamp(momentRoundedToHour(date.date(diff)));
  }

  // Otherwise assume hour scale and start at 6am on given day
  return momentToTimestamp(momentRoundedToHour(date, 6));
}

/**
 * Safely retrieves the number of subdivisions for a given timeline scale.
 * @param scale The timeline scale to for which to retrieve subdivision default
 * @returns number of subdivisions for the given scale
 */
export function getScaleUnitCount(scale: TimelineScale): number {
  return Object.keys(defaultTimelineUnitCount).includes(scale)
    ? defaultTimelineUnitCount[scale]
    : defaultTimelineUnitCount.hours;
}

/**
 * Safely retrieves the number of subdivisions for a given timeline scale.
 * @param scale The timeline scale to for which to retrieve subdivision default
 * @returns number of subdivisions for the given scale
 */
export function getScaleSubdivisions(scale: TimelineScale): number {
  return Object.keys(scaleSubdivisionDefaults).includes(scale)
    ? scaleSubdivisionDefaults[scale]
    : 1;
}

/**
 * Retrieves the time stamp increment for a given timeline scale and subdivision count.
 * @param scale The timeline scale for which to get the time increment
 * @param subdivisions Desired number of subdivisions for the scale; falls back to the scale's default if not provided
 * @returns THe number of timestamp units for the given scale and subdivision count
 */
export function getScaleTimeIncrement(
  scale: TimelineScale,
  subdivisions?: number,
): number {
  // Use either the subdivisions provided or the default for the scale provided
  const subdivisionsCount = subdivisions ?? getScaleSubdivisions(scale);

  // If days, default to subdivided by 3 hours (8 per day)
  if (scale === 'days') {
    return (hour * 24) / subdivisionsCount;
  }

  // If weeks, default to subdivided by days (7 per week)
  if (scale === 'weeks') {
    return (hour * 24 * 7) / subdivisionsCount;
  }

  // Otherwise assume hour scale and default to subdivided by 15 minutes (4 per hour)
  return hour / subdivisionsCount;
}

/**
 * Given a start scale and unitCount, determine the size ratio for one unit on the scale, optionally subdivided.
 * @returns The ratio of the unit of time to the overall timeline size.
 */
export function getScaleUnitSize(
  scale: TimelineScale,
  unitCount?: number,
  subdivisions?: number,
): number {
  return (
    getScaleTimeIncrement(scale, subdivisions ?? 1) /
    (getScaleTimeIncrement(scale, 1) * (unitCount ?? getScaleUnitCount(scale)))
  );
}

/** Determines the timeline position based on the time and related context */
export function getPositionFromTime(
  time: number,
  startTime: number,
  scale: TimelineScale,
  unitCount?: number,
): number {
  const scaledTimeIncrement = getScaleTimeIncrement(scale, 1);
  const elapsedTime = time - startTime;
  return elapsedTime / (scaledTimeIncrement * unitCount);
}

/** Determines the timestamp based on a timeline position and related context */
export function getTimeFromPosition(
  position: number,
  startTime: number,
  scale: TimelineScale,
  subdivisions?: number,
): number {
  const scaledTimeIncrement = getScaleTimeIncrement(scale, 1);
  return startTime + position * (scaledTimeIncrement * subdivisions);
}

export function getScaledLabel(time: number, scale: TimelineScale): string {
  const date = moment(time);

  switch (scale) {
    case 'days':
      return date.format('ddd, MMM D');
    case 'weeks':
      return `Week of ${date.format('MMM D')}`;
    case 'hours':
    default:
      return date.format('h:mm a');
  }
}
